import JSZip from "jszip";
import saveAs from "file-saver";
import {
  ProbeOutcome,
  describeConnectionFailure,
  describeRefusedReply,
  describeServerReply,
  describeUnsentRequest,
  isConnectionFailure,
  isRefusedReply,
  isUnsentRequest,
} from "./connectionDiagnosis";
import { describeLoginError } from "./loginErrorMessage";

export type BrowserEnvironment = {
  pageUrl: string;
  pageOrigin: string;
  pageProtocol: string;
  userAgent: string;
  language: string;
  timeZone: string;
  online: boolean;
};

export type LoginLogInput = {
  generatedAt: Date;
  failedAt: Date;
  // The routing variant this build serves (REACT_APP_VERSION, e.g. "v1"); there is no build number.
  appVariant: string;
  // Which of the page's three flows failed: login, first-login set password, forced change.
  flow: string;
  method: string;
  requestUrl: string;
  configKey: string;
  attempts: number;
  error: unknown;
  probe?: ProbeOutcome;
  environment: BrowserEnvironment;
};

export type LogFile = { name: string; content: string };

const MAX_TEXT_LENGTH = 1000;

export const readBrowserEnvironment = (): BrowserEnvironment => ({
  // origin + path only: the query string could carry tokens
  pageUrl: `${window.location.origin}${window.location.pathname}`,
  pageOrigin: window.location.origin,
  pageProtocol: window.location.protocol,
  userAgent: navigator.userAgent,
  language: navigator.language,
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  online: navigator.onLine,
});

const clip = (value: string): string =>
  value.length > MAX_TEXT_LENGTH
    ? `${value.slice(0, MAX_TEXT_LENGTH)}… [${
        value.length - MAX_TEXT_LENGTH
      } more characters not shown]`
    : value;

const textOrUndefined = (value: unknown): string | undefined =>
  typeof value === "string" ? clip(value) : undefined;

const withoutUndefined = (entries: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(entries).filter(([, value]) => value !== undefined));

// The body of a reply is never copied as text. The reply that fails to decode (PARSING_ERROR) is a
// successful one, and a successful login reply is the one that holds the session token. The one
// string kept is a CUSTOM_ERROR's, which this application wrote itself.
const summariseData = (data: unknown, status: unknown): unknown => {
  if (typeof data === "string") {
    return status === "CUSTOM_ERROR" ? clip(data) : undefined;
  }
  if (typeof data !== "object" || data === null) {
    return undefined;
  }
  const { message, error } = data as { message?: unknown; error?: unknown };
  const picked = withoutUndefined({
    message: textOrUndefined(message),
    error: textOrUndefined(error),
  });
  return Object.keys(picked).length > 0 ? picked : undefined;
};

// Only the fields a support person needs. The server's reply is never copied wholesale: a reply
// could echo back what was sent or carry a token, so only its `message` / `error` text is kept,
// and every text is cut to a fixed length.
export const summariseError = (error: unknown): unknown => {
  if (error === undefined || error === null) {
    return null;
  }
  if (typeof error !== "object") {
    return clip(String(error));
  }
  const shape = error as Record<string, unknown>;
  return withoutUndefined({
    status:
      typeof shape.status === "number" || typeof shape.status === "string"
        ? shape.status
        : undefined,
    originalStatus: typeof shape.originalStatus === "number" ? shape.originalStatus : undefined,
    name: textOrUndefined(shape.name),
    message: textOrUndefined(shape.message),
    error: textOrUndefined(shape.error),
    data: summariseData(shape.data, shape.status),
  });
};

type Analysis = {
  summary: string;
  causes: string[];
  checks: string[];
  serverCheck: { result: ProbeOutcome; label: string } | null;
};

const analyse = (input: LoginLogInput): Analysis | null => {
  const { environment } = input;
  if (isConnectionFailure(input.error) && input.probe) {
    const diagnosis = describeConnectionFailure({
      method: input.method,
      requestUrl: input.requestUrl,
      page: { protocol: environment.pageProtocol, origin: environment.pageOrigin },
      online: environment.online,
      probe: input.probe,
      configKey: input.configKey,
    });
    return {
      summary: diagnosis.summary,
      causes: diagnosis.causes,
      checks: diagnosis.checks,
      serverCheck: { result: input.probe, label: diagnosis.probeLabel },
    };
  }
  // The server answered; the analysis names the release mismatch the refusal reports.
  if (isRefusedReply(input.error)) {
    return {
      ...describeRefusedReply({
        error: input.error,
        method: input.method,
        requestUrl: input.requestUrl,
        page: { protocol: environment.pageProtocol, origin: environment.pageOrigin },
      }),
      serverCheck: null,
    };
  }
  if (isUnsentRequest(input.error)) {
    return {
      ...describeUnsentRequest({ requestUrl: input.requestUrl, configKey: input.configKey }),
      serverCheck: null,
    };
  }
  // The server answered, so there is nothing to probe; a gateway status still points at a cause.
  const reply = describeServerReply({
    error: input.error,
    method: input.method,
    requestUrl: input.requestUrl,
    page: { protocol: environment.pageProtocol, origin: environment.pageOrigin },
  });
  return reply ? { ...reply, serverCheck: null } : null;
};

// The login body (email, password) is never part of the log: only the request address and a
// short, allowlisted summary of the error the browser or server reported are recorded.
const buildDetails = (input: LoginLogInput) => {
  const { environment } = input;
  const analysis = analyse(input);
  const raw = summariseError(input.error);
  const status = (raw as { status?: unknown } | null)?.status;
  return {
    generatedAt: input.generatedAt.toISOString(),
    loginFailedAt: input.failedAt.toISOString(),
    app: { variant: input.appVariant },
    flow: input.flow,
    page: {
      url: environment.pageUrl,
      userAgent: environment.userAgent,
      language: environment.language,
      timeZone: environment.timeZone,
      online: environment.online,
    },
    request: {
      method: input.method,
      url: input.requestUrl,
      apiAddressSetting: input.configKey,
      attempts: input.attempts,
    },
    error: {
      shownToUser: clip(
        describeLoginError(input.error, input.requestUrl, environment.pageOrigin).headline,
      ),
      status: status ?? "N/A",
      raw,
    },
    serverCheck: analysis?.serverCheck
      ? { ...analysis.serverCheck, checkedAt: input.generatedAt.toISOString() }
      : null,
    summary: analysis?.summary ?? null,
    likelyCauses: analysis?.causes ?? [],
    whatToCheck: analysis?.checks ?? [],
  };
};

type Details = ReturnType<typeof buildDetails>;

const bullets = (items: string[]): string[] => items.map((item) => `- ${item}`);

const buildReport = (details: Details): string => {
  const lines = [
    "LiftNet offline login log",
    `Generated: ${details.generatedAt} (${details.page.timeZone})`,
    `Login failed at: ${details.loginFailedAt}`,
    `Screen: ${details.flow}`,
    "",
    "WHAT THE USER SAW",
    details.error.shownToUser,
    "",
    "REQUEST",
    `${details.request.method} ${details.request.url}`,
    `API address setting: ${details.request.apiAddressSetting}`,
    `Attempts: ${details.request.attempts}`,
    "",
    "ERROR",
    `Status: ${String(details.error.status)}`,
    JSON.stringify(details.error.raw, null, 2),
  ];
  if (details.serverCheck) {
    lines.push(
      "",
      "SERVER CHECK",
      `${details.serverCheck.label} (checked at ${details.serverCheck.checkedAt}, after the login failed)`,
    );
  }
  if (details.summary) {
    lines.push("", "SUMMARY", details.summary);
  }
  if (details.likelyCauses.length > 0) {
    lines.push("", "LIKELY CAUSES", ...bullets(details.likelyCauses));
  }
  if (details.whatToCheck.length > 0) {
    lines.push("", "WHAT TO CHECK", ...bullets(details.whatToCheck));
  }
  lines.push(
    "",
    "ENVIRONMENT",
    `App variant: ${details.app.variant}`,
    `Page: ${details.page.url}`,
    `Browser: ${details.page.userAgent}`,
    `Language: ${details.page.language}`,
    `Browser reports online: ${details.page.online}`,
    "",
  );
  return lines.join("\n");
};

export const buildLoginLogFiles = (input: LoginLogInput): LogFile[] => {
  const details = buildDetails(input);
  return [
    { name: "report.txt", content: buildReport(details) },
    { name: "details.json", content: `${JSON.stringify(details, null, 2)}\n` },
  ];
};

export const buildLoginLogZip = async (input: LoginLogInput): Promise<ArrayBuffer> => {
  const zip = new JSZip();
  buildLoginLogFiles(input).forEach((file) => zip.file(file.name, file.content));
  return zip.generateAsync({ type: "arraybuffer" });
};

export const loginLogFileName = (generatedAt: Date): string =>
  `liftnet-login-log-${generatedAt
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d+Z$/, "Z")}.zip`;

export const downloadLoginLogBundle = async (input: LoginLogInput): Promise<void> => {
  const zip = await buildLoginLogZip(input);
  saveAs(new Blob([zip], { type: "application/zip" }), loginLogFileName(input.generatedAt));
};
